/**
 * 自动翻译 Hook
 * 用于非中/英语言的内容自动翻译
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { translateText, translateBatch } from '@/services/translateService';

// 需要自动翻译的语言（非中英文）
const AUTO_TRANSLATE_LANGUAGES = ['ru', 'uk', 'fr', 'es', 'pt', 'ja', 'ko', 'ar', 'uz'];

/**
 * 检查是否需要自动翻译
 * @param {string} lang - 当前语言
 * @returns {boolean}
 */
export const needsAutoTranslation = (lang) => {
  return AUTO_TRANSLATE_LANGUAGES.includes(lang);
};

/**
 * 获取本地化字段值（同步版本）
 * 优先返回对应语言的已有翻译，如果没有则返回中文或英文
 * @param {Object} item - 数据对象
 * @param {string} field - 字段名
 * @param {string} lang - 当前语言
 * @returns {string} 字段值
 */
export const getLocalizedFieldSync = (item, field, lang) => {
  if (!item) return '';
  
  // 中文直接返回
  if (lang === 'zh') {
    return item[field] || '';
  }
  
  // 英文
  if (lang === 'en') {
    return item[`${field}_en`] || item[field] || '';
  }
  
  // 其他语言：优先尝试获取对应字段，如果没有则返回英文或中文
  const langField = `${field}_${lang}`;
  if (item[langField]) {
    return item[langField];
  }
  
  // 回退到英文或中文
  return item[`${field}_en`] || item[field] || '';
};

/**
 * 单字段自动翻译 Hook
 * @param {Object} item - 数据对象
 * @param {string} field - 字段名
 * @param {string} lang - 当前语言
 * @returns {{ text: string, isTranslating: boolean, isAutoTranslated: boolean }}
 */
export const useAutoTranslateField = (item, field, lang) => {
  const [text, setText] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);
  const [isAutoTranslated, setIsAutoTranslated] = useState(false);
  const lastKeyRef = useRef('');
  
  useEffect(() => {
    if (!item) {
      setText('');
      return;
    }
    
    // 生成缓存键
    const key = `${item.id || ''}-${field}-${lang}`;
    if (key === lastKeyRef.current) return;
    lastKeyRef.current = key;
    
    // 中文或英文直接使用
    if (lang === 'zh') {
      setText(item[field] || '');
      setIsAutoTranslated(false);
      return;
    }
    
    if (lang === 'en') {
      setText(item[`${field}_en`] || item[field] || '');
      setIsAutoTranslated(false);
      return;
    }
    
    // 其他语言：先检查是否有现成翻译
    const langField = `${field}_${lang}`;
    if (item[langField]) {
      setText(item[langField]);
      setIsAutoTranslated(false);
      return;
    }
    
    // 需要自动翻译
    const sourceText = item[`${field}_en`] || item[field] || '';
    if (!sourceText) {
      setText('');
      return;
    }
    
    // 先显示源文本，然后异步翻译
    setText(sourceText);
    setIsTranslating(true);
    
    translateText(sourceText, lang, 'auto')
      .then(translated => {
        setText(translated);
        setIsAutoTranslated(true);
      })
      .catch(() => {
        // 翻译失败保留源文本
      })
      .finally(() => {
        setIsTranslating(false);
      });
      
  }, [item, field, lang]);
  
  return { text, isTranslating, isAutoTranslated };
};

/**
 * 多字段自动翻译 Hook
 * @param {Object} item - 数据对象
 * @param {string[]} fields - 字段名数组
 * @param {string} lang - 当前语言
 * @returns {{ translations: Object, isTranslating: boolean }}
 */
export const useAutoTranslateFields = (item, fields, lang) => {
  const [translations, setTranslations] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);
  const lastKeyRef = useRef('');
  
  useEffect(() => {
    if (!item || !fields || fields.length === 0) {
      setTranslations({});
      return;
    }
    
    const key = `${item.id || ''}-${fields.join(',')}-${lang}`;
    if (key === lastKeyRef.current) return;
    lastKeyRef.current = key;
    
    // 中文直接使用
    if (lang === 'zh') {
      const result = {};
      fields.forEach(field => {
        result[field] = item[field] || '';
      });
      setTranslations(result);
      return;
    }
    
    // 英文
    if (lang === 'en') {
      const result = {};
      fields.forEach(field => {
        result[field] = item[`${field}_en`] || item[field] || '';
      });
      setTranslations(result);
      return;
    }
    
    // 其他语言：收集需要翻译的字段
    const result = {};
    const needTranslate = {};
    
    fields.forEach(field => {
      const langField = `${field}_${lang}`;
      if (item[langField]) {
        result[field] = item[langField];
      } else {
        const sourceText = item[`${field}_en`] || item[field] || '';
        result[field] = sourceText;
        if (sourceText) {
          needTranslate[field] = sourceText;
        }
      }
    });
    
    setTranslations(result);
    
    // 如果有需要翻译的字段
    if (Object.keys(needTranslate).length > 0) {
      setIsTranslating(true);
      translateBatch(needTranslate, lang)
        .then(translated => {
          setTranslations(prev => ({ ...prev, ...translated }));
        })
        .finally(() => {
          setIsTranslating(false);
        });
    }
    
  }, [item, fields, lang]);
  
  return { translations, isTranslating };
};

/**
 * 获取本地化字段的增强版本（支持自动翻译标记）
 * 用于需要显示翻译状态的场景
 */
export const getLocalizedFieldWithAuto = (item, field, lang) => {
  if (!item) return { text: '', needsTranslation: false };
  
  if (lang === 'zh') {
    return { text: item[field] || '', needsTranslation: false };
  }
  
  if (lang === 'en') {
    return { text: item[`${field}_en`] || item[field] || '', needsTranslation: false };
  }
  
  // 其他语言
  const langField = `${field}_${lang}`;
  if (item[langField]) {
    return { text: item[langField], needsTranslation: false };
  }
  
  // 需要自动翻译
  const sourceText = item[`${field}_en`] || item[field] || '';
  return { text: sourceText, needsTranslation: !!sourceText };
};

export default {
  needsAutoTranslation,
  getLocalizedFieldSync,
  useAutoTranslateField,
  useAutoTranslateFields,
  getLocalizedFieldWithAuto,
};
